/**
 * Native React canvas for the 3-element high-lift foil — the React replacement for the vanilla
 * `<canvas>` drawing in the vendored webui `preview.ts` (`render` / `drawPropeller` / `fill` /
 * `world`). It draws the deployed geometry (main / vane / flaperon), the actuator-disk + slipstream
 * band, the deploy track + hinge markers, the B-spline control nets, and — when `showAnnotations`
 * is on — the gap-metric overlay: the slot throat lines and reattachment markers read from the
 * deployed contours.
 *
 * Pure presentation over the pure-TS geometry package: every contour comes from
 * `buildConfiguration`; clicks are mapped to a picked surface via `probe.ts`. No iframe, no DOM
 * module state.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  resolveDeploy,
  deployTransform,
  pivotAtFraction,
  buildConfiguration,
  toPoints,
  pointInPoly,
  nearestSurfacePoint,
  FOIL_VIEW,
  letterbox,
  zoomViewAbout,
  zoomLevel,
  blowingOf,
  type Configuration,
  type HighLiftAirfoil,
  type V2,
  type ViewRect,
} from '../geometry';
import { useHighliftTheme, type HighliftPalette } from './useHighliftTheme';
import { foilHandleDescriptors, type FoilHandleDescriptor, type HandleXformKind } from './useFoilDesign';

/**
 * Wheel-zoom limits (× the default `FOIL_VIEW` span). 1 = the home frame.
 * MIN_ZOOM < 1 lets you zoom OUT past home to see a larger slice of the flow field
 * (paired with the wider server-side CLIP_BOUNDS); MAX_ZOOM is effectively
 * unbounded for near-infinite zoom IN (float precision holds well past 1000×).
 */
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 5000;

/** Result of a canvas click: which element was hit (or the flow), with the world point + wall foot. */
export interface FoilPick {
  /** The element under the click, or `null` when the click landed in the flow (outside all elements). */
  element: 'main' | 'vane' | 'flap' | null;
  /** Clicked point in world (chord) coordinates [x, z]. */
  world: V2;
  /** Nearest wall point on any element (the wall-normal foot); only meaningful when `element` is null. */
  wall: V2;
}

export interface FoilGeometryCanvasProps {
  /** The live design + operating point to draw. */
  config: HighLiftAirfoil;
  /**
   * The pre-built deployed geometry for `config` — `buildConfiguration(config)`. Passed in
   * (rather than rebuilt here) so a caller that ALSO needs it (e.g. `useFoilDesign`, which every
   * consumer of this canvas already calls) computes it exactly ONCE per config change and shares
   * the same result, instead of two independent `useMemo`s both rebuilding the full multi-element
   * contour on every pointermove during a drag (FXC-9009 — this was the single biggest cost on
   * the drag hot path: a real rebuild, duplicated, with no debounce).
   */
  configuration: Configuration;
  /**
   * Master toggle for ALL annotation overlays: the grid + axis labels, the deploy
   * track, the B-spline control nets, the hinge crosshairs, the gap-metric overlay
   * (slot-throat lines + reattachment markers), and the drag handles + hint. When
   * off, only the element fills + the actuator-disk/slipstream + the flow remain —
   * a clean foil view. Default true.
   */
  showAnnotations?: boolean;
  /** Click-to-pick callback (element under the click, or the flow + nearest wall point). */
  onPickSurface?: (pick: FoilPick) => void;
  /**
   * Drag callback for the control-point handles. Receives the (string) handle id and the dragged
   * position in STOWED config coords (the deploy transform is already inverted here). Wire this to
   * `useFoilDesign().setHandleDescriptor`; omitting it disables dragging (handles still draw).
   */
  onSetControlPoint?: (handleId: string, worldX: number, worldZ: number) => void;
  /** The selected element id — its handles draw emphasised (active ring), driving the Shape panel. */
  selectedElementId?: string | null;
  /** Hover callback for the "what am I dragging" read-out: the hovered/dragged descriptor, or null. */
  onHoverHandle?: (handle: { id: string; label: string; world: V2; elementId: string } | null) => void;
  /** Select callback fired on pointer-down over a handle (so clicking a handle selects its element). */
  onSelectElement?: (elementId: string) => void;
  /**
   * The chord-coordinate view rect to draw (defaults to `FOIL_VIEW`). Pass the
   * same rect to `<FlowVisLayer view={…}/>` so the field stays aligned. When
   * `onViewChange` is set, the mouse wheel zooms this rect about the cursor.
   */
  view?: ViewRect;
  /** Wheel-zoom callback — receives the new view rect. Omitting it disables wheel zoom. */
  onViewChange?: (view: ViewRect) => void;
  /** Draw the text labels (control-point ids, gap metrics, handle labels, hint). Default true. */
  showLabels?: boolean;
  /** CSS pixel width of the canvas. Default 760. */
  width?: number;
  /** CSS pixel height of the canvas. Default 360. */
  height?: number;
  /**
   * Emits a CSS `clip-path` (evenodd: full canvas rect minus the smooth element
   * contours) whenever the geometry/view changes. Apply it to the sibling
   * `<FlowVisLayer>` wrapper so the LIC field is clipped to the SMOOTH foil
   * exterior — otherwise the field's grid-resolution mask (NaN hole for RANS,
   * damped interior for the NN) shows a staircased boundary just outside the
   * foil that an opaque foil-on-top can't cover (and a fixed skirt fails when
   * zoomed in). The clip is in CSS px matching the shared `view` letterbox.
   */
  onFieldClip?: (clip: string | null) => void;
}

/**
 * Imperative handle for driving a smooth deploy-angle ANIMATION without a React state change per
 * frame (FXC-9009). The normal path (dragging, editing) flows through props/`config` and re-renders
 * the whole workbench — fine for discrete edits, but at ~60fps a full-surface re-render per frame
 * (3D loft rebuild, inspector panels, AG-grid pivot, …) drops the effective rate to a few fps on
 * slower engines (notably Safari/WebKit), so the sweep visibly STEPS in big jumps. `previewDeploy`
 * instead rebuilds ONLY the deployed contours + handles at the given angle and redraws the canvas
 * directly — no setState, no field-clip emit, so nothing else in the tree re-renders. The caller
 * commits the final angle to real state once, on stop.
 */
export interface FoilGeometryCanvasHandle {
  /** Redraw the canvas at `angleDeg` of motion WITHOUT going through React state. `axis` selects
   *  which operation DOF the angle drives: `deploy` (default — the vane+flaperon deploy track) or
   *  `flaperon` (the flaperon/plain-flap deflection, the moving DOF for a CS-seeded plain flap). */
  previewDeploy: (angleDeg: number, axis?: 'deploy' | 'flaperon') => void;
  /** Return subsequent React-driven draws to the committed operating point. */
  clearDeployPreview: () => void;
}

const sub = (a: V2, b: V2): V2 => [a[0] - b[0], a[1] - b[1]];
const norm = (v: V2): number => Math.hypot(v[0], v[1]);
const unit = (v: V2): V2 => {
  const m = norm(v);
  return m > 0 ? [v[0] / m, v[1] / m] : [0, 0];
};
const dot = (a: V2, b: V2): number => a[0] * b[0] + a[1] * b[1];

/**
 * World→pixel transform for `view`, letterboxed (port of the webui `world()`).
 * `view` is the live (possibly wheel-zoomed) rect, shared with the LIC flow layer
 * so the field and the geometry map to the same pixels — see geometry/foilView.ts.
 */
function makeWorld(view: ViewRect, w: number, h: number) {
  const { s, ox, oy } = letterbox(view, w, h);
  return {
    s,
    ox,
    oy,
    x: (x: number) => ox + (x - view.xmin) * s,
    y: (y: number) => h - (oy + (y - view.ymin) * s),
  };
}

type World = ReturnType<typeof makeWorld>;

/** Invert a pixel coordinate (offsetX/offsetY) back to a world point (port of the webui click math). */
function pixelToWorld(view: ViewRect, t: World, h: number, offsetX: number, offsetY: number): V2 {
  return [view.xmin + (offsetX - t.ox) / t.s, view.ymin + (h - offsetY - t.oy) / t.s];
}

/** Rigid 2-D motion as point fns: `forward` maps stowed→deployed, `inverse` deployed→stowed. */
interface RigidXform {
  forward: (p: V2) => V2;
  inverse: (p: V2) => V2;
}

/** Identity rigid transform (drag maps directly to config). */
export const IDENTITY_XFORM: RigidXform = { forward: (p) => [p[0], p[1]], inverse: (p) => [p[0], p[1]] };

/**
 * The deploy transform for the current operating point, as forward + inverse point fns. This is the
 * SAME rigid motion `buildConfiguration`'s `move` applies to the vane assembly: translate the stowed
 * pivot to `pivotF`, then rotate by the deploy angle about it. The inverse (used to map a dragged
 * handle in the deployed frame back to the stowed config it stores) is the exact algebraic inverse:
 * `T(p) = R(p − pivot0) + pivotF` ⇒ `T⁻¹(world) = R⁻¹(world − pivotF) + pivot0`. When deployAngleDeg
 * is 0 and there's no track travel this reduces to identity, so the drag maps directly.
 */
export function deployRigid(cfg: HighLiftAirfoil): RigidXform {
  const { track, motion } = resolveDeploy(cfg);
  const deployAngleDeg = cfg.operation.deployAngleDeg;
  const forwardFn = deployTransform(track, motion, deployAngleDeg);
  const full = motion.fullDeployAngleDeg !== 0 ? motion.fullDeployAngleDeg : 1;
  const pivot0 = track.pts[0] ?? [0, 0];
  const pivotF = pivotAtFraction(track, motion, deployAngleDeg / full);
  // R⁻¹ uses the negated deploy angle (forward uses -deployRad ⇒ inverse uses +deployRad).
  const invRad = (deployAngleDeg * Math.PI) / 180.0;
  const ci = Math.cos(invRad);
  const si = Math.sin(invRad);
  return {
    forward: (p) => forwardFn([p])[0] ?? p,
    inverse: (world) => {
      const dx = world[0] - pivotF[0];
      const dy = world[1] - pivotF[1];
      return [dx * ci - dy * si + pivot0[0], dx * si + dy * ci + pivot0[1]];
    },
  };
}

/** Rotate a single point by `angleRad` about `center` (port of the geometry `rotate`). */
function rotatePoint(p: V2, angleRad: number, center: V2): V2 {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  const dx = p[0] - center[0];
  const dy = p[1] - center[1];
  return [dx * c - dy * s + center[0], dx * s + dy * c + center[1]];
}

/**
 * The full forward+inverse transform for ONE handle's deploy-frame kind (AC-H data-driven model).
 *  - `deploy`: vane control points + the flaperon-hinge marker ride the deploy transform only (`move`).
 *  - `flap-nose`: the flaperon nose tip + cut points are first rotated about the flaperon's own hinge
 *    (relative to the assembly) and then carried by `move`, exactly as `buildConfiguration`'s
 *    `deploy = move(rotate(·, flapRad, fPivot))`.
 *  - `identity`: the deploy-hinge + actuator-disk handles are drawn at their raw config coords.
 */
export function handleXform(kind: HandleXformKind, cfg: HighLiftAirfoil): RigidXform {
  switch (kind) {
    case 'identity':
      return IDENTITY_XFORM;
    case 'flap-nose': {
      const move = deployRigid(cfg);
      const fPivot = cfg.design.flaperonHinge.pivot;
      const flapRad = -(cfg.operation.flaperonAngleDeg * Math.PI) / 180.0;
      return {
        // stowed → (rotate about hinge) → (deploy move) → deployed
        forward: (p) => move.forward(rotatePoint(p, flapRad, fPivot)),
        // deployed → (inverse move) → (rotate back about hinge) → stowed
        inverse: (world) => rotatePoint(move.inverse(world), -flapRad, fPivot),
      };
    }
    // 'deploy': vane control points + flaperon-hinge ride the deploy transform only.
    default:
      return deployRigid(cfg);
  }
}

/** Fill + stroke a closed polygon (port of the webui `fill()`). */
function fillPoly(
  ctx: CanvasRenderingContext2D,
  pts: { x: number; y: number }[],
  t: World,
  faceColor: string,
  edge: string,
): void {
  if (pts.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(t.x(pts[0]!.x), t.y(pts[0]!.y));
  for (let i = 1; i < pts.length; i++) ctx.lineTo(t.x(pts[i]!.x), t.y(pts[i]!.y));
  ctx.closePath();
  ctx.fillStyle = faceColor;
  ctx.fill();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = edge;
  ctx.stroke();
}

/** A hinge cross-hair marker (port of the webui pivot drawing). */
function drawHinge(ctx: CanvasRenderingContext2D, t: World, pivot: V2, color: string): void {
  const px = t.x(pivot[0]);
  const py = t.y(pivot[1]);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(px, py, 5, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(px - 8, py);
  ctx.lineTo(px + 8, py);
  ctx.moveTo(px, py - 8);
  ctx.lineTo(px, py + 8);
  ctx.stroke();
}

/** A dashed control-net polygon with labelled control points (port of the webui `drawControlNet`). */
function drawControlNet(
  ctx: CanvasRenderingContext2D,
  t: World,
  pts: V2[],
  color: string,
  closed: boolean,
  showLabels: boolean,
): void {
  if (pts.length === 0) return;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(t.x(pts[0]![0]), t.y(pts[0]![1]));
  for (let i = 1; i < pts.length; i++) ctx.lineTo(t.x(pts[i]![0]), t.y(pts[i]![1]));
  if (closed) ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = color;
  ctx.font = '10px system-ui';
  pts.forEach((p, i) => {
    ctx.beginPath();
    ctx.arc(t.x(p[0]), t.y(p[1]), 3.5, 0, 2 * Math.PI);
    ctx.fill();
    if (showLabels) ctx.fillText(`P${i}`, t.x(p[0]) + 5, t.y(p[1]) - 5);
  });
}

/** A draggable handle with its DEPLOYED screen-frame world position (for drawing + hit-testing). */
interface ScreenHandle extends FoilHandleDescriptor {
  /** The handle's position in the DEPLOYED (drawn) world frame. */
  deployed: V2;
}

/** Handle family → accent colour, so a handle reads as belonging to its element. */
function handleColor(family: FoilHandleDescriptor['family'], palette: HighliftPalette): string {
  switch (family) {
    case 'vane':
      return palette.vaneControl;
    case 'flaperon':
      return palette.flaperonControl;
    case 'deploy':
      return palette.deployHinge;
    case 'propeller':
      return palette.blue;
    default:
      return palette.handle;
  }
}

/**
 * Draw one bigger, instructive control-point handle: a filled ring in its element colour, an
 * enlarged hover/active highlight ring, and the handle label. The handle is drawn at its DEPLOYED
 * screen position so it sits on the rendered shape (the drag math inverts the deploy transform).
 */
function drawHandle(
  ctx: CanvasRenderingContext2D,
  t: World,
  handle: ScreenHandle,
  palette: HighliftPalette,
  highlighted: boolean,
  selected: boolean,
  showLabels: boolean,
): void {
  const cx = t.x(handle.deployed[0]);
  const cy = t.y(handle.deployed[1]);
  const base = handleColor(handle.family, palette);
  const r = highlighted ? 7 : selected ? 6 : 5;
  // Selected element's handles carry a dashed emphasis ring in the element colour (matches the
  // prototype's `active` ring), even when not hovered, so the Shape-panel selection reads on the shape.
  if (selected && !highlighted) {
    ctx.beginPath();
    ctx.arc(cx, cy, r + 4, 0, 2 * Math.PI);
    ctx.strokeStyle = base;
    ctx.lineWidth = 1.25;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (highlighted) {
    // enlarged hover ring
    ctx.beginPath();
    ctx.arc(cx, cy, r + 4, 0, 2 * Math.PI);
    ctx.strokeStyle = palette.handleHover;
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  // filled dot with a light halo so it reads on any element fill
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, 2 * Math.PI);
  ctx.fillStyle = base;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = highlighted ? palette.handleHover : palette.surface;
  ctx.stroke();
  // label
  if (showLabels) {
    ctx.font = highlighted ? '600 11px system-ui' : '10px system-ui';
    ctx.fillStyle = highlighted ? palette.ink : palette.ink3;
    ctx.fillText(handle.label, cx + r + 4, cy - r - 2);
  }
}

/** True when the disk is loaded (Δp/q∞ ≠ 0 ⇒ thrust or suction). */
function propActive(cfg: HighLiftAirfoil): boolean {
  return cfg.operation.diskLoading !== 0;
}

/** Draw the actuator disk (translucent thickness rectangle) + slipstream band (port of `drawPropeller`). */
function drawPropeller(
  ctx: CanvasRenderingContext2D,
  t: World,
  cfg: HighLiftAirfoil,
  palette: HighliftPalette,
  view: ViewRect,
): void {
  // Draw EVERY blowing source in the collection (the default has one = the legacy propeller). The
  // slipstream `active` is the operation-level loading (per-source loading is a later tranche).
  const active = propActive(cfg);
  for (const src of blowingOf(cfg)) {
    const r = src.diameter / 2;
    const hh = src.diameter / 32; // half-thickness (thickness = diameter/16)
    const rad = (src.tiltDeg * Math.PI) / 180;
    const jx = Math.cos(rad);
    const jz = Math.sin(rad); // axis / jet (thickness) direction
    const px = -Math.sin(rad);
    const pz = Math.cos(rad); // disk face (radius) direction
    const a = { x: src.x - r * px, z: src.z - r * pz };
    const b = { x: src.x + r * px, z: src.z + r * pz };
    if (active) {
      const L = view.xmax - src.x + 0.2;
      ctx.fillStyle = palette.slipstream;
      ctx.beginPath();
      ctx.moveTo(t.x(a.x), t.y(a.z));
      ctx.lineTo(t.x(b.x), t.y(b.z));
      ctx.lineTo(t.x(b.x + L * jx), t.y(b.z + L * jz));
      ctx.lineTo(t.x(a.x + L * jx), t.y(a.z + L * jz));
      ctx.closePath();
      ctx.fill();
    }
    ctx.fillStyle = active ? palette.diskOn : palette.diskOff;
    ctx.beginPath();
    ctx.moveTo(t.x(a.x - hh * jx), t.y(a.z - hh * jz));
    ctx.lineTo(t.x(b.x - hh * jx), t.y(b.z - hh * jz));
    ctx.lineTo(t.x(b.x + hh * jx), t.y(b.z + hh * jz));
    ctx.lineTo(t.x(a.x + hh * jx), t.y(a.z + hh * jz));
    ctx.closePath();
    ctx.fill();
  }
}

/** Index of the smallest-x (LE) vertex of a contour. */
function leadingEdgeIdx(contour: V2[]): number {
  let k = 0;
  for (let i = 1; i < contour.length; i++) if (contour[i]![0] < contour[k]![0]) k = i;
  return k;
}

/** Index of the largest-x (TE) vertex of a contour. */
function trailingEdgeIdx(contour: V2[]): number {
  let k = 0;
  for (let i = 1; i < contour.length; i++) if (contour[i]![0] > contour[k]![0]) k = i;
  return k;
}

/** Closest vertex-to-vertex segment between two contours (the slot throat) — mirrors gapMetrics. */
function throatSegment(a: V2[], b: V2[]): { pa: V2; pb: V2 } {
  let best = Infinity;
  let pa: V2 = a[0] ?? [0, 0];
  let pb: V2 = b[0] ?? [0, 0];
  for (const va of a) {
    for (const vb of b) {
      const d = norm(sub(va, vb));
      if (d < best) {
        best = d;
        pa = va;
        pb = vb;
      }
    }
  }
  return { pa, pb };
}

/** Local downstream-oriented wake tangent at a TE vertex (mirrors gapMetrics.wakeDirection). */
function wakeDirection(poly: V2[], teIdx: number, span = 3): V2 {
  const before = poly[Math.max(0, teIdx - span)] ?? poly[teIdx]!;
  const after = poly[Math.min(poly.length - 1, teIdx + span)] ?? poly[teIdx]!;
  const fwd = before[0] < after[0] ? before : after;
  const dir = unit(sub(poly[teIdx]!, fwd));
  return dir[0] >= 0 ? dir : [-dir[0], -dir[1]];
}

/**
 * Draw the gap-metric overlay for one slot: the throat line (cyan, with its width labelled) and a
 * reattachment marker — the downstream LE projected onto the upstream wake line (pink). Geometry is
 * recomputed from the deployed contours so it matches `gapMetrics` exactly.
 */
function drawSlotAnnotation(
  ctx: CanvasRenderingContext2D,
  t: World,
  upstream: V2[],
  downstream: V2[],
  palette: HighliftPalette,
): void {
  // No slot without both contours: a bare/reduced design (e.g. a seeded clean
  // foil, or vane/flaperon removed) leaves an element contour empty, and the
  // LE/TE index lookups below would dereference undefined.
  if (upstream.length === 0 || downstream.length === 0) return;
  // throat: closest approach (the slot gap width)
  const { pa, pb } = throatSegment(upstream, downstream);
  const gap = norm(sub(pa, pb));
  ctx.strokeStyle = palette.throat;
  ctx.fillStyle = palette.throat;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(t.x(pa[0]), t.y(pa[1]));
  ctx.lineTo(t.x(pb[0]), t.y(pb[1]));
  ctx.stroke();
  ctx.setLineDash([]);
  const mx = (t.x(pa[0]) + t.x(pb[0])) / 2;
  const my = (t.y(pa[1]) + t.y(pb[1])) / 2;
  ctx.font = '10px system-ui';
  ctx.fillText(`gap ${gap.toFixed(3)}`, mx + 6, my - 4);

  // reattachment marker: downstream LE + its foot on the upstream wake line.
  const upTeIdx = trailingEdgeIdx(upstream);
  const upTe = upstream[upTeIdx]!;
  const dnLe = downstream[leadingEdgeIdx(downstream)]!;
  const wake = wakeDirection(upstream, upTeIdx);
  // foot of dnLe onto the wake ray (upTe + alpha·wake)
  const alpha = dot(sub(dnLe, upTe), wake);
  const foot: V2 = [upTe[0] + alpha * wake[0], upTe[1] + alpha * wake[1]];
  // the wake line (a short ray downstream from the upstream TE)
  ctx.strokeStyle = palette.reattach;
  ctx.fillStyle = palette.reattach;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 4]);
  ctx.beginPath();
  ctx.moveTo(t.x(upTe[0]), t.y(upTe[1]));
  ctx.lineTo(t.x(foot[0] + 0.04 * wake[0]), t.y(foot[1] + 0.04 * wake[1]));
  ctx.stroke();
  // the normal offset from the wake line to the downstream LE
  ctx.beginPath();
  ctx.moveTo(t.x(foot[0]), t.y(foot[1]));
  ctx.lineTo(t.x(dnLe[0]), t.y(dnLe[1]));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(t.x(dnLe[0]), t.y(dnLe[1]), 3.5, 0, 2 * Math.PI);
  ctx.fill();
}

/**
 * Pointer hit radius (CSS px) for grabbing a control-point handle — the dot radius (~5px) + ~16px
 * of hit slop, per the design spec (control hit targets ≥ dot radius + ~16px). Generous so the
 * stable-root pointer hit-test reliably grabs the nearest handle even on a dense control net.
 */
const HANDLE_HIT_PX = 16;

export const FoilGeometryCanvas = forwardRef<FoilGeometryCanvasHandle, FoilGeometryCanvasProps>(function FoilGeometryCanvas({
  config,
  configuration,
  showAnnotations = true,
  onPickSurface,
  onSetControlPoint,
  selectedElementId = null,
  onHoverHandle,
  onSelectElement,
  view = FOIL_VIEW,
  onViewChange,
  showLabels = true,
  width = 760,
  height = 360,
  onFieldClip,
}: FoilGeometryCanvasProps, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const palette = useHighliftTheme();

  // Field-clip emission: keep the callback in a ref so `draw`'s deps stay stable,
  // and only emit when the clip string actually changes (avoids parent churn).
  const onFieldClipRef = useRef(onFieldClip);
  onFieldClipRef.current = onFieldClip;
  const lastClipRef = useRef<string | null>(null);

  // Deployed contours + control points for the current operating point. `configuration` is a PROP
  // now (see FoilGeometryCanvasProps) — built once by the caller, not rebuilt here.
  const deploy = useMemo(() => resolveDeploy(config), [config]);

  // The DATA-DRIVEN draggable handles (AC-H), each at its DEPLOYED screen position (forward-
  // transformed from the stowed config it stores). The drag math inverts the same per-handle deploy
  // transform to write back. Added elements' handles appear here automatically (descriptors are
  // generated from the element collection), so no per-element wiring is needed.
  const screenHandles = useMemo<ScreenHandle[]>(
    () =>
      foilHandleDescriptors(config).map((h) => ({
        ...h,
        deployed: handleXform(h.xform, config).forward(h.stowed),
      })),
    [config],
  );
  const previewDeployAngleRef = useRef<number | null>(null);
  const previewDeployAxisRef = useRef<'deploy' | 'flaperon'>('deploy');
  const configRef = useRef(config);
  configRef.current = config;
  // Pointer logic must read the same frame the canvas last drew, including imperative previews.
  const interactionConfigRef = useRef(config);
  const interactionConfigurationRef = useRef(configuration);
  const interactionHandlesRef = useRef(screenHandles);
  if (previewDeployAngleRef.current == null) {
    interactionConfigRef.current = config;
    interactionConfigurationRef.current = configuration;
    interactionHandlesRef.current = screenHandles;
  }

  // Which handle (if any) the pointer is hovering / dragging — drives the highlight + cursor + the
  // "what am I dragging" read-out. String ids (descriptor ids), not the fixed enum.
  const [hoverId, setHoverId] = useState<string | null>(null);
  // Captures the handle's id AND its xform kind ONCE at pointer-down (not re-derived by id lookup
  // on every pointermove): `handleXform`'s default case falls back to the deploy-hinge rotation,
  // so any transient miss on a by-id re-lookup mid-drag would silently swap an `identity`-frame
  // handle (e.g. the actuator disk) onto the deploy rotation — a large, wrong offset once
  // deployAngleDeg is non-zero. Capturing at gesture-start removes that lookup (and its fallback)
  // from the hot per-pointermove path entirely, fixing both the correctness risk and a redundant
  // linear search every move.
  const draggingRef = useRef<{ id: string; xform: HandleXformKind } | null>(null);
  const draggable = !!onSetControlPoint;
  // Drag events are rAF-throttled: native pointermove can fire far faster than the display refreshes
  // (some trackpads/mice coalesce well above 60Hz), and each `onSetControlPoint` call triggers a real
  // parent-state mutation + re-render, not just a local redraw. Without this, a fast drag gesture queues
  // many more state updates than frames ever get painted, and the UI visibly lags behind the cursor
  // (FXC-9009 "dragging is REALLY slow"). Coalescing to one flush per animation frame caps the update
  // rate to what can actually be displayed, using only the LATEST pointer position each frame.
  const rafRef = useRef<number | null>(null);
  const pendingDragRef = useRef<{
    id: string;
    x: number;
    z: number;
    hover: { id: string; label: string; world: V2; elementId: string } | null;
  } | null>(null);
  const flushDrag = useCallback(() => {
    rafRef.current = null;
    const pending = pendingDragRef.current;
    pendingDragRef.current = null;
    if (!pending) return;
    onSetControlPoint?.(pending.id, pending.x, pending.z);
    if (pending.hover) onHoverHandle?.(pending.hover);
  }, [onSetControlPoint, onHoverHandle]);
  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);
  // Pan-drag (left-drag on empty space when zoomed in): records the grab pixel +
  // the view at grab time; `panMovedRef` suppresses the click-pick that follows a pan.
  const panRef = useRef<{ x: number; y: number; startView: ViewRect } | null>(null);
  const panMovedRef = useRef(false);
  const pannable = !!onViewChange;

  // The full render, parameterised by the deployed geometry + handles to draw. `config` (closed over)
  // is used only for deploy-angle-INVARIANT bits (the actuator disk, the deploy track, the stowed
  // hinge pivot), so passing overridden `conf`/`handles` is enough to animate the deploy angle.
  // `emitClip` is suppressed during imperative preview: the field-clip callback is a parent setState,
  // which would re-render the whole surface every frame and defeat the point of the imperative path.
  const drawWith = useCallback((conf: Configuration, handles: ScreenHandle[], emitClip: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    // Assigning canvas.width/height REALLOCATES + clears the whole backing store every time — even
    // when the value is unchanged. That's a notable per-frame cost on Safari/WebKit (far worse than
    // Chrome, and worse again on Retina where dpr=2 ⇒ 4× the pixels), which showed up as jank during
    // the deploy animation where width/height/dpr never change. Only resize when they actually
    // differ; the setTransform + clearRect below still run every frame to reset the context (a
    // resize clears the transform) and wipe the previous frame.
    const targetW = Math.round(width * dpr);
    const targetH = Math.round(height * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const t = makeWorld(view, width, height);

    ctx.clearRect(0, 0, width, height);

    // grid + axis labels. Grid lines span the full DRAWN extent (not the data view)
    // so a zoomed-in view stays fully gridded; ticks step every 0.2c / 0.1c.
    // All of this is "annotation" — hidden when showAnnotations is off for a clean
    // foil + flow view.
    if (showAnnotations) {
      const gxMin = view.xmin - t.ox / t.s;
      const gxMax = gxMin + width / t.s;
      const gyMin = view.ymin - t.oy / t.s;
      const gyMax = gyMin + height / t.s;
      ctx.lineWidth = 1;
      ctx.strokeStyle = palette.grid;
      ctx.fillStyle = palette.gridLabel;
      ctx.font = '11px system-ui';
      for (let gx = Math.ceil(gxMin / 0.2) * 0.2; gx <= gxMax + 1e-9; gx += 0.2) {
        ctx.beginPath();
        ctx.moveTo(t.x(gx), t.y(gyMax));
        ctx.lineTo(t.x(gx), t.y(gyMin));
        ctx.stroke();
        if (showLabels) ctx.fillText(gx.toFixed(1), t.x(gx) - 8, height - 4);
      }
      for (let gy = Math.ceil(gyMin / 0.1) * 0.1; gy <= gyMax + 1e-9; gy += 0.1) {
        ctx.beginPath();
        ctx.moveTo(t.x(gxMin), t.y(gy));
        ctx.lineTo(t.x(gxMax), t.y(gy));
        ctx.stroke();
        if (showLabels) ctx.fillText(gy.toFixed(1), 2, t.y(gy) - 3);
      }

      // deploy track (the carriage path) — only meaningful when it has travel.
      if (deploy.track.length > 0 && deploy.track.pts.length > 1) {
        ctx.strokeStyle = palette.track;
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(t.x(deploy.track.pts[0]![0]), t.y(deploy.track.pts[0]![1]));
        for (let i = 1; i < deploy.track.pts.length; i++) {
          ctx.lineTo(t.x(deploy.track.pts[i]![0]), t.y(deploy.track.pts[i]![1]));
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // disk + slipstream band (behind the elements) — physical blowing source, kept
    // visible regardless of the annotation toggle.
    drawPropeller(ctx, t, config, palette, view);

    // EVERY element in the collection (main + vane(s) + flaperon(s) + any added foils), filled by
    // kind. Iterating conf.elements means added elements render without further wiring.
    const { flaperonPivot } = conf;
    for (const el of conf.elements) {
      const [face, edge] =
        el.kind === 'main-cove' ? [palette.mainFace, palette.mainEdge]
        : el.kind === 'vane-bspline' ? [palette.vaneFace, palette.vaneEdge]
        : [palette.flapFace, palette.flapEdge];
      fillPoly(ctx, toPoints(el.contour), t, face, edge);
    }

    // Emit a clip-path (full rect MINUS the smooth element contours, evenodd) so the
    // sibling LIC field is clipped to the smooth foil exterior. This removes the
    // staircased field boundary (RANS NaN-hole / NN damped-interior grid edge) at any
    // zoom, independent of grid resolution. Px coords match the shared view letterbox.
    if (emitClip) {
      let d = `M0 0L${width} 0L${width} ${height}L0 ${height}Z`;
      for (const el of conf.elements) {
        const pts = toPoints(el.contour);
        if (pts.length < 3) continue;
        d += `M${t.x(pts[0]!.x).toFixed(1)} ${t.y(pts[0]!.y).toFixed(1)}`;
        for (let i = 1; i < pts.length; i++) d += `L${t.x(pts[i]!.x).toFixed(1)} ${t.y(pts[i]!.y).toFixed(1)}`;
        d += 'Z';
      }
      const clip = `path(evenodd, "${d}")`;
      if (clip !== lastClipRef.current) {
        lastClipRef.current = clip;
        onFieldClipRef.current?.(clip);
      }
    }

    // Overlays (control nets, hinge crosshairs, gap-metric overlay, drag handles +
    // hint) — all annotation; hidden when showAnnotations is off.
    if (showAnnotations) {
      // B-spline control nets, per element (vane = closed loop, flaperon = open nose).
      for (const el of conf.elements) {
        if (el.controls.length === 0) continue;
        const closed = el.kind === 'vane-bspline';
        const color = closed ? palette.vaneControl : palette.flaperonControl;
        drawControlNet(ctx, t, el.controls, color, closed, showLabels);
      }

      // hinge markers
      drawHinge(ctx, t, config.design.deployHinge.pivot, palette.deployHinge);
      drawHinge(ctx, t, flaperonPivot, palette.flaperonHinge);

      // gap-metric overlay (slot throat lines + reattachment markers), main→vane→flaperon slots
      drawSlotAnnotation(ctx, t, conf.main, conf.vane, palette);
      drawSlotAnnotation(ctx, t, conf.vane, conf.flaperon, palette);

      // draggable control-point handles (on top), with the hovered/dragging one highlighted and the
      // SELECTED element's handles emphasised (dashed ring) so the Shape-panel selection reads here.
      if (draggable) {
        const activeId = draggingRef.current?.id ?? hoverId;
        for (const h of handles) {
          const isSelected = selectedElementId != null && h.elementId === selectedElementId;
          drawHandle(ctx, t, h, palette, h.id === activeId, isSelected, showLabels);
        }
        // instructive hint line
        if (showLabels) {
          ctx.font = '11px system-ui';
          ctx.fillStyle = palette.ink3;
          ctx.fillText('drag the circular handles to reshape the splines', 6, 14);
        }
      }
    }
  }, [config, deploy, showAnnotations, view, showLabels, width, height, palette, draggable, hoverId, selectedElementId]);

  // Imperative deploy-preview (FXC-9009): rebuild ONLY the deployed geometry + handles at `angleDeg`
  // and redraw, bypassing React state entirely so nothing else in the surface re-renders per frame.
  // Reads the latest `config`/`drawWith` via refs since it's invoked outside the render cycle.
  const drawWithRef = useRef(drawWith);
  drawWithRef.current = drawWith;
  const drawPreviewDeploy = useCallback((angleDeg: number, axis: 'deploy' | 'flaperon' = 'deploy') => {
    const c: HighLiftAirfoil = {
      ...configRef.current,
      operation: {
        ...configRef.current.operation,
        ...(axis === 'flaperon' ? { flaperonAngleDeg: angleDeg } : { deployAngleDeg: angleDeg }),
      },
    };
    const conf = buildConfiguration(c);
    const handles: ScreenHandle[] = foilHandleDescriptors(c).map((h) => ({
      ...h,
      deployed: handleXform(h.xform, c).forward(h.stowed),
    }));
    interactionConfigRef.current = c;
    interactionConfigurationRef.current = conf;
    interactionHandlesRef.current = handles;
    drawWithRef.current(conf, handles, false);
  }, []);

  // Normal (React-driven) render: the deployed geometry + handles from props/memos, clip emitted.
  const draw = useCallback(() => {
    const previewAngle = previewDeployAngleRef.current;
    if (previewAngle != null) {
      drawPreviewDeploy(previewAngle, previewDeployAxisRef.current);
      return;
    }
    drawWith(configuration, screenHandles, true);
  }, [drawPreviewDeploy, drawWith, configuration, screenHandles]);

  useEffect(() => {
    draw();
  }, [draw]);

  useImperativeHandle(
    ref,
    (): FoilGeometryCanvasHandle => ({
      previewDeploy: (angleDeg: number, axis: 'deploy' | 'flaperon' = 'deploy') => {
        previewDeployAngleRef.current = angleDeg;
        previewDeployAxisRef.current = axis;
        drawPreviewDeploy(angleDeg, axis);
      },
      clearDeployPreview: () => {
        previewDeployAngleRef.current = null;
        previewDeployAxisRef.current = 'deploy';
      },
    }),
    [drawPreviewDeploy],
  );

  // Mouse-wheel zoom about the cursor. Attached natively (not via React's onWheel,
  // which is passive) so we can preventDefault the page scroll. Each tick rescales
  // `view` toward MAX_ZOOM/clamps at MIN_ZOOM and reports it via onViewChange; both
  // the foil and the LIC field re-render at the new view (the streamlines recompute).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onViewChange) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const t = makeWorld(view, width, height);
      const [ax, az] = pixelToWorld(view, t, height, ev.clientX - rect.left, ev.clientY - rect.top);
      const factor = Math.exp(ev.deltaY * 0.0015); // wheel up (deltaY<0) ⇒ zoom in
      const cur = zoomLevel(view);
      const target = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, cur / factor));
      const eff = cur / target;
      if (Math.abs(eff - 1) < 1e-4) return; // clamped at a limit — nothing to do
      onViewChange(zoomViewAbout(view, eff, ax, az));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [view, onViewChange, width, height]);

  /** Pointer offset (CSS px) within the canvas. */
  const pointerOffset = (ev: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  };

  /** Look up the current screen handle by descriptor id (drag/hover both key off the id). */
  const handleById = useCallback(
    (id: string | null): ScreenHandle | undefined => (id == null ? undefined : interactionHandlesRef.current.find((h) => h.id === id)),
    [],
  );

  /**
   * The handle under a pixel position (nearest within HANDLE_HIT_PX), or null. The hit-test runs on
   * the STABLE canvas root (the dots are wiped/redrawn each frame), so a drag started here survives
   * the redraw — the original "dot holding the listener dies mid-drag" bug doesn't apply.
   */
  const hitHandle = useCallback(
    (offsetX: number, offsetY: number): string | null => {
      const t = makeWorld(view, width, height);
      let best: string | null = null;
      let bestD = HANDLE_HIT_PX;
      for (const h of interactionHandlesRef.current) {
        const dx = t.x(h.deployed[0]) - offsetX;
        const dy = t.y(h.deployed[1]) - offsetY;
        const d = Math.hypot(dx, dy);
        if (d <= bestD) {
          bestD = d;
          best = h.id;
        }
      }
      return best;
    },
    [view, width, height],
  );

  const handlePointerDown = useCallback(
    (ev: React.PointerEvent<HTMLCanvasElement>) => {
      const { x, y } = pointerOffset(ev);
      const capture = () => {
        try {
          ev.currentTarget.setPointerCapture(ev.pointerId);
        } catch {
          /* some pointers (e.g. synthetic) can't be captured — drag still tracks */
        }
      };
      const hit = draggable ? hitHandle(x, y) : null;
      if (hit) {
        // Capture this handle's xform kind NOW — see the draggingRef comment above.
        const desc = handleById(hit);
        draggingRef.current = { id: hit, xform: desc?.xform ?? 'identity' };
        setHoverId(hit);
        // Selecting a handle selects its owning element (drives the Shape panel + handle emphasis).
        if (desc && onSelectElement) onSelectElement(desc.elementId);
        capture();
        return;
      }
      // Empty-space drag pans the view whenever it's been zoomed in OR out (left or
      // middle button). At the exact home frame a left-drag stays a surface pick.
      if (onViewChange && (ev.button === 0 || ev.button === 1) && Math.abs(zoomLevel(view) - 1) > 0.01) {
        panRef.current = { x, y, startView: view };
        panMovedRef.current = false;
        capture();
      }
    },
    [draggable, hitHandle, handleById, onSelectElement, onViewChange, view],
  );

  const handlePointerMove = useCallback(
    (ev: React.PointerEvent<HTMLCanvasElement>) => {
      const { x, y } = pointerOffset(ev);
      // Pan in progress: translate the view so the grabbed world point follows the cursor.
      const pan = panRef.current;
      if (pan && onViewChange) {
        const { s } = makeWorld(pan.startView, width, height);
        const dx = (x - pan.x) / s;
        const dy = (y - pan.y) / s;
        if (Math.abs(x - pan.x) > 3 || Math.abs(y - pan.y) > 3) panMovedRef.current = true;
        onViewChange({
          xmin: pan.startView.xmin - dx,
          xmax: pan.startView.xmax - dx,
          ymin: pan.startView.ymin + dy, // screen-y is down; world-y is up
          ymax: pan.startView.ymax + dy,
        });
        return;
      }
      const t = makeWorld(view, width, height);
      const dragging = draggingRef.current;
      if (dragging && onSetControlPoint) {
        // px → deployed world → (invert THIS handle's OWN xform, captured at pointer-down — not
        // re-looked-up here, so there's no fallback case that could pick the wrong transform).
        const world = pixelToWorld(view, t, height, x, y);
        const stowed = handleXform(dragging.xform, interactionConfigRef.current).inverse(world);
        // Label/elementId are display-only — a lookup miss here just skips the readout update,
        // it can never affect where the handle actually ends up (that's `dragging.id`/`stowed` below).
        const desc = onHoverHandle ? handleById(dragging.id) : undefined;
        // Queue the latest position and flush at most once per frame — see the rafRef comment above.
        pendingDragRef.current = {
          id: dragging.id,
          x: stowed[0],
          z: stowed[1],
          hover: desc ? { id: desc.id, label: desc.label, world: stowed, elementId: desc.elementId } : null,
        };
        if (rafRef.current == null) rafRef.current = requestAnimationFrame(flushDrag);
        return;
      }
      if (draggable) {
        const over = hitHandle(x, y);
        if (over !== hoverId) {
          setHoverId(over);
          if (onHoverHandle) {
            const desc = handleById(over);
            onHoverHandle(desc ? { id: desc.id, label: desc.label, world: desc.stowed, elementId: desc.elementId } : null);
          }
        }
      }
    },
    [onSetControlPoint, draggable, hitHandle, handleById, onHoverHandle, hoverId, onViewChange, view, width, height, flushDrag],
  );

  const endDrag = useCallback((ev: React.PointerEvent<HTMLCanvasElement>) => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // Flush any queued-but-unpainted move synchronously on release, so the handle lands exactly
    // where the pointer was let go rather than snapping back to the last painted frame.
    flushDrag();
    if (draggingRef.current || panRef.current) {
      try {
        ev.currentTarget.releasePointerCapture(ev.pointerId);
      } catch {
        /* pointer already released */
      }
      draggingRef.current = null;
      panRef.current = null;
    }
  }, [flushDrag]);

  const handleClick = useCallback(
    (ev: React.MouseEvent<HTMLCanvasElement>) => {
      // A drag that panned the view is not a surface pick.
      if (panMovedRef.current) {
        panMovedRef.current = false;
        return;
      }
      if (!onPickSurface) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = ev.clientX - rect.left;
      const offsetY = ev.clientY - rect.top;
      // A click that grabbed a handle (drag, even a zero-distance one) is not a surface pick.
      if (draggable && hitHandle(offsetX, offsetY)) return;
      const t = makeWorld(view, width, height);
      const world = pixelToWorld(view, t, height, offsetX, offsetY);
      const { main, vane, flaperon } = interactionConfigurationRef.current;
      const hit = (
        [
          ['main', main],
          ['vane', vane],
          ['flap', flaperon],
        ] as [FoilPick['element'], V2[]][]
      ).find(([, poly]) => pointInPoly(world[0], world[1], poly));
      if (hit) {
        onPickSurface({ element: hit[0], world, wall: world });
      } else {
        const wall = nearestSurfacePoint(world[0], world[1], [main, vane, flaperon]);
        onPickSurface({ element: null, world, wall });
      }
    },
    [onPickSurface, view, width, height, draggable, hitHandle],
  );

  // Cursor: grabbing while dragging/panning, grab over a handle or when the zoomed
  // view is pannable, else the pick/default cursor.
  const zoomedForPan = pannable && zoomLevel(view) > 1.01;
  const cursor = draggingRef.current || panRef.current
    ? 'grabbing'
    : draggable && hoverId
      ? 'grab'
      : zoomedForPan
        ? 'grab'
        : onPickSurface
          ? 'crosshair'
          : 'default';

  const interactive = draggable || pannable;
  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      onPointerDown={interactive ? handlePointerDown : undefined}
      onPointerMove={interactive ? handlePointerMove : undefined}
      onPointerUp={interactive ? endDrag : undefined}
      onPointerCancel={interactive ? endDrag : undefined}
      onPointerLeave={
        draggable
          ? () => {
              if (!draggingRef.current) {
                setHoverId(null);
                onHoverHandle?.(null);
              }
            }
          : undefined
      }
      style={{
        width: `${width}px`,
        height: `${height}px`,
        display: 'block',
        touchAction: interactive ? 'none' : undefined,
        cursor,
      }}
    />
  );
});
